import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { addDays, summarize, todayIso, type Contact, type LogEntry, type LogType, type Person, type SchemaOptions, type User } from '@crmsy/shared';
import { logTypeLabel, useT, type Key } from '../i18n/index.js';
import { db } from '../lib/db.js';
import { dispatch } from '../lib/actions.js';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { normalize } from '../lib/search.js';
import { toast } from '../lib/toast.js';
import { LOG_TYPES } from '@crmsy/shared';
import { ContactPanel } from '../components/ContactPanel.jsx';
import { RevertConfirm } from '../components/Dialogs.jsx';
import { OriginBadge, StatusBadge, actorName, canRevert, when } from '../components/Timeline.jsx';

const sel = 'rounded-lg border border-line bg-paper px-2 py-1 text-xs outline-none focus:border-accent';
type Range = 'all' | 'today' | 'week' | 'month';

/** Log (§6.4): newest first, filters by actor / contact / type / date, search, revert with confirmation. */
export function LogView({ user, search }: { user: User; search: string }) {
  const t = useT();
  const log = useLiveQuery(() => db.log.toArray(), [], undefined);
  const contacts = useLiveQuery(() => db.contacts.toArray(), [], [] as Contact[]);
  const people = useLiveQuery(() => db.people.toArray(), [], [] as Person[]);
  const schemaRows = useLiveQuery(() => db.schema.toArray(), [], []);
  const schema = useMemo<SchemaOptions>(() => Object.fromEntries(schemaRows.map((r) => [r.property, r.options])), [schemaRows]);
  const byId = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);

  const [type, setType] = useState<LogType | ''>('');
  const [actor, setActor] = useState('');
  const [range, setRange] = useState<Range>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(0);
  const [panelId, setPanelId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<LogEntry | null>(null);

  const actors = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of log ?? []) {
      const key = e.actorEmail ?? e.actor ?? e.source;
      if (!seen.has(key)) seen.set(key, actorName(e, people));
    }
    return [...seen.entries()];
  }, [log, people]);

  const rows = useMemo(() => {
    if (!log) return [];
    const today = todayIso();
    const since = range === 'today' ? today : range === 'week' ? addDays(today, -7) : range === 'month' ? addDays(today, -30) : '';
    const q = normalize(search.trim());
    const name = (id: string) => people.find((p) => p.id === id)?.name ?? id;
    return log
      .filter((e) => showArchived || !e.archived)
      .filter((e) => !type || e.type === type)
      .filter((e) => !actor || (e.actorEmail ?? e.actor ?? e.source) === actor)
      .filter((e) => !since || e.at.slice(0, 10) >= since)
      .filter((e) => {
        if (!q) return true;
        const c = e.contactId ? byId.get(e.contactId) : undefined;
        const hay = normalize([e.text, e.name, summarize(e, name), c?.companyName ?? '', c?.contact ?? ''].join(' '));
        return hay.includes(q);
      })
      .sort((a, b) => b.at.localeCompare(a.at))
      .slice(0, 500);
  }, [log, showArchived, type, actor, range, search, byId, people]);

  useEffect(() => {
    setSelected((s) => Math.min(s, Math.max(0, rows.length - 1)));
  }, [rows.length]);
  useEffect(() => {
    document.querySelector<HTMLElement>(`[data-log-index="${selected}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const current = rows[selected] ?? null;
  const panelContact = panelId ? (byId.get(panelId) ?? null) : null;

  useKeys(
    {
      j: () => setSelected((s) => Math.min(s + 1, rows.length - 1)),
      k: () => setSelected((s) => Math.max(s - 1, 0)),
      ArrowDown: () => setSelected((s) => Math.min(s + 1, rows.length - 1)),
      ArrowUp: () => setSelected((s) => Math.max(s - 1, 0)),
      Enter: () => {
        if (current?.contactId && byId.has(current.contactId)) setPanelId(current.contactId);
      },
      r: () => {
        if (current && canRevert(current, user)) setConfirm(current);
      },
    },
    { priority: PRIORITY.view, enabled: !panelContact && !confirm },
  );

  const revert = (e: LogEntry) => void dispatch('revert', { entryId: e.id }).catch((err: unknown) => toast(err instanceof Error ? err.message : String(err), { kind: 'error' }));
  const name = (id: string) => people.find((p) => p.id === id)?.name ?? id;

  if (!log) return <div className="p-6 text-sm text-muted">{t('table.opening')}</div>;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-paper px-4 py-2">
        <select value={type} onChange={(e) => setType(e.target.value as LogType | '')} className={sel} aria-label={t('log.type')}>
          <option value="">{t('log.allTypes')}</option>
          {LOG_TYPES.map((lt) => (
            <option key={lt} value={lt}>{logTypeLabel(lt)}</option>
          ))}
        </select>
        <select value={actor} onChange={(e) => setActor(e.target.value)} className={sel} aria-label={t('log.who')}>
          <option value="">{t('log.everyone')}</option>
          {actors.map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <select value={range} onChange={(e) => setRange(e.target.value as Range)} className={sel} aria-label={t('log.when')}>
          <option value="all">{t('log.anyTime')}</option>
          <option value="today">{t('log.today')}</option>
          <option value="week">{t('log.week7')}</option>
          <option value="month">{t('log.month30')}</option>
        </select>
        <button type="button" className="chip" data-on={showArchived} onClick={() => setShowArchived((v) => !v)}>{t('log.removedNotes')}</button>
        <span className="ml-auto text-xs text-muted">{t('log.entries', { n: rows.length === 500 ? '500+' : rows.length })} · <kbd>r</kbd> {t('log.revertHint')}</span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted">{search ? t('log.noMatch') : t('log.empty')}</div>
        ) : (
          <table className="w-full min-w-[900px] border-separate border-spacing-0 text-sm">
            <thead className="sticky top-0 z-10 bg-surface">
              <tr>
                {(['log.when', 'log.who', 'log.contact', 'log.what', 'log.source', 'log.status'] as Key[]).map((h) => (
                  <th key={h} className="border-b border-line px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted">{t(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((e, i) => {
                const c = e.contactId ? byId.get(e.contactId) : undefined;
                return (
                  <tr
                    key={e.id}
                    data-log-index={i}
                    data-log-type={e.type}
                    onClick={() => setSelected(i)}
                    className={`border-b border-line hover:bg-surface ${i === selected ? 'row-selected' : ''} ${e.archived ? 'opacity-50' : ''}`}
                  >
                    <td className="whitespace-nowrap border-b border-line px-3 py-2 text-xs text-muted">{when(e.at)}</td>
                    <td className="whitespace-nowrap border-b border-line px-3 py-2">{actorName(e, people)}</td>
                    <td className="max-w-[220px] truncate border-b border-line px-3 py-2">
                      {c ? (
                        <button type="button" className="text-left hover:text-accent-dark hover:underline" onClick={() => setPanelId(c.id)}>
                          {c.companyName || c.contact || '(no name)'}
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="border-b border-line px-3 py-2">
                      <span className="mr-1.5 text-xs text-muted">{logTypeLabel(e.type)}</span>
                      <span className={e.reverted ? 'line-through text-muted' : ''}>{summarize(e, name)}</span>
                    </td>
                    <td className="border-b border-line px-3 py-2 text-xs text-muted">{e.source === 'notion' ? t('log.sourceNotion') : e.source}</td>
                    <td className="border-b border-line px-3 py-2"><OriginBadge e={e} /> <StatusBadge e={e} /></td>
                    <td className="border-b border-line px-3 py-2 text-right">
                      {canRevert(e, user) && (
                        <button type="button" className="btn" onClick={() => setConfirm(e)}>
                          {t('timeline.revert')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {confirm && <RevertConfirm entry={confirm} people={people} onConfirm={() => revert(confirm)} onClose={() => setConfirm(null)} />}
      {panelContact && <ContactPanel contact={panelContact} people={people} schema={schema} user={user} onClose={() => setPanelId(null)} />}
    </div>
  );
}
